<script lang="ts">
  /**
   * Heatmap dia-da-semana × hora-do-dia: ritmo operacional da empresa.
   * Revela picos de entrada/saída/intervalo e padrões de turno.
   */
  import type { PunchHeatmap } from '$core/metrics/metrics.types';

  export let heatmap: PunchHeatmap;

  const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  const HOURS = Array.from({ length: 24 }, (_, h) => h);

  $: max = Math.max(1, heatmap.max);

  // Linha mais ativa pra destacar como insight
  function cellAt(d: number, h: number): number {
    return heatmap.counts[d]?.[h] ?? 0;
  }

  $: rowTotals = heatmap.counts.map(row => row.reduce((a, b) => a + b, 0));
  $: peakWeekday = rowTotals.indexOf(Math.max(...rowTotals));

  /** Hora de pico global (qualquer dia). */
  $: peakHour = (() => {
    let bestH = 0;
    let bestVal = 0;
    for (let h = 0; h < 24; h++) {
      let total = 0;
      for (let d = 0; d < 7; d++) total += cellAt(d, h);
      if (total > bestVal) {
        bestVal = total;
        bestH = h;
      }
    }
    return bestH;
  })();

  function colorFor(value: number): string {
    if (value === 0) return '#F5F5F5';
    const ratio = Math.min(1, value / max);
    // Interpola purple-100 (#7F3D7D) com branco
    const r = Math.round(255 - (255 - 0x7f) * ratio);
    const g = Math.round(255 - (255 - 0x3d) * ratio);
    const b = Math.round(255 - (255 - 0x7d) * ratio);
    return `rgb(${r},${g},${b})`;
  }
</script>

<div class="chart">
  <header class="chart__head">
    <div>
      <h3 class="chart__title">Ritmo operacional do ponto</h3>
      <p class="chart__subtitle">Densidade de marcações por dia da semana × hora do dia</p>
    </div>
    <div class="chart__kpis">
      <div class="kpi">
        <span class="kpi__label">Dia mais ativo</span>
        <span class="kpi__value">{WEEKDAYS[peakWeekday]}</span>
      </div>
      <div class="kpi">
        <span class="kpi__label">Hora de pico</span>
        <span class="kpi__value">{String(peakHour).padStart(2, '0')}h</span>
      </div>
    </div>
  </header>

  <div class="grid-wrap">
    <div class="grid">
      <div class="grid__corner" aria-hidden="true"></div>
      <div class="grid__hours">
        {#each HOURS as h}
          <div class="grid__hour" class:grid__hour--peak={h === peakHour}>{String(h).padStart(2, '0')}</div>
        {/each}
      </div>

      {#each WEEKDAYS as wd, dIdx}
        <div class="grid__day" class:grid__day--peak={dIdx === peakWeekday}>{wd}</div>
        <div class="grid__row">
          {#each HOURS as h}
            <div
              class="cell"
              style="background: {colorFor(cellAt(dIdx, h))};"
              title="{wd} {String(h).padStart(2, '0')}h: {cellAt(dIdx, h)} marcações"
            ></div>
          {/each}
        </div>
      {/each}
    </div>

    <div class="legend">
      <span class="legend__label">Menos</span>
      <div class="legend__bar">
        {#each [0, 0.15, 0.3, 0.5, 0.7, 0.85, 1] as t}
          <div class="legend__cell" style="background: {colorFor(t * max)};"></div>
        {/each}
      </div>
      <span class="legend__label">Mais</span>
    </div>
  </div>
</div>

<style>
  .chart { display: flex; flex-direction: column; gap: var(--ptt-space-3); }
  .chart__head { display: flex; justify-content: space-between; gap: var(--ptt-space-4); flex-wrap: wrap; align-items: flex-end; }
  .chart__title { margin: 0; font-size: 1rem; font-weight: 700; color: var(--ptt-gray-300); }
  .chart__subtitle { margin: 2px 0 0; font-size: 0.8125rem; color: var(--ptt-gray-100); }
  .chart__kpis { display: flex; gap: var(--ptt-space-4); flex-wrap: wrap; }
  .kpi { display: flex; flex-direction: column; line-height: 1.1; }
  .kpi__label { font-size: 0.6875rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--ptt-gray-100); }
  .kpi__value { font-size: 1.125rem; font-weight: 700; color: var(--ptt-gray-300); margin-top: 2px; }

  .grid-wrap { display: flex; flex-direction: column; gap: var(--ptt-space-3); }
  .grid {
    display: grid;
    grid-template-columns: 40px 1fr;
    gap: 4px;
    align-items: center;
  }
  .grid__corner { height: 18px; }
  .grid__hours {
    display: grid;
    grid-template-columns: repeat(24, 1fr);
    gap: 2px;
    font-size: 0.625rem;
    color: var(--ptt-gray-100);
  }
  .grid__hour {
    text-align: center;
    line-height: 18px;
  }
  .grid__hour--peak { color: var(--ptt-purple-100); font-weight: 700; }
  .grid__day {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--ptt-gray-200);
    line-height: 1;
  }
  .grid__day--peak { color: var(--ptt-purple-100); }
  .grid__row {
    display: grid;
    grid-template-columns: repeat(24, 1fr);
    gap: 2px;
  }
  .cell {
    aspect-ratio: 1.6 / 1;
    border-radius: 3px;
    transition: transform 120ms ease;
  }
  .cell:hover {
    transform: scale(1.18);
    box-shadow: 0 0 0 1px var(--ptt-gray-300);
  }

  .legend {
    display: flex;
    align-items: center;
    gap: var(--ptt-space-2);
    font-size: 0.75rem;
    color: var(--ptt-gray-100);
    margin-left: 44px;
  }
  .legend__bar {
    display: flex;
    gap: 1px;
  }
  .legend__cell {
    width: 18px;
    height: 10px;
    border-radius: 2px;
  }
</style>
